import { Component, computed, inject, input, output, Signal, WritableSignal } from '@angular/core';
import { TooltipPosition } from '@angular/material/tooltip';

import { AppMaterialModule } from '../../material.module';
import { ImageVersionService } from 'src/app/features/image/services/image-version.service';
import { LoadingPageService } from 'src/app/infrastructure/services/loading-services/loading-page.service';
import { SharedModule } from '../../shared.module';

@Component({
    selector: 'app_image-box',
    template: '',
})
export abstract class _ImageBoxComponent
{
    protected abstract _loadingPageService: LoadingPageService;

    abstract border$$: Signal<boolean> | WritableSignal<boolean>;
    abstract guid$$: Signal<string | null> | WritableSignal<string | null>;
    abstract hideOnLoading$$: Signal<boolean> | WritableSignal<boolean>;
    abstract loading$$: Signal<boolean> | WritableSignal<boolean>;
    abstract size$$: Signal<string> | WritableSignal<string>;
    abstract tooltip$$: Signal<string | null> | WritableSignal<string | null>;
    abstract tooltipPosition$$: Signal<TooltipPosition> | WritableSignal<TooltipPosition>;
    abstract url$$: Signal<string | null> | WritableSignal<string | null>;
    abstract version$$: Signal<string | null> | WritableSignal<string | null>;
    abstract guidPath$$: Signal<string | null>;

    input_border$$ = input<boolean>(false, {alias: 'border'});
    input_guid$$ = input<string | null>(null, {alias: 'guid'});
    input_hideOnLoading$$ = input<boolean>(false, {alias: 'hideOnLoading'});
    input_size$$ = input<string>('', {alias: 'size'});
    input_tooltip$$ = input<string | null>(null, {alias: 'tooltip'});
    input_tooltipPosition$$ = input<TooltipPosition>('above', {alias: 'tooltipPosition'});
    input_url$$ = input<string | null>(null, {alias: 'url'});
    input_version$$ = input<string | null>(null, {alias: 'version'});

    // Concreet (niet abstract, i.t.t. de rest hierboven): een optionele 'geldig drop-doel'-hint-rand,
    // los van border$$ (die alleen bepaalt OF er een kader is, niet de kleur). Concreet zodat elke
    // subklasse 'm automatisch meekrijgt zonder een eigen override-regel — nu alleen gebruikt door
    // image-box.component.html zelf (lokale .highlight-class/stijl, zie image-box-details.component.ts
    // dragActive$$ voor de aanroeper) en simpelweg doorgegeven door image-box-link.component.html.
    input_highlight$$ = input<boolean>(false, {alias: 'highlight'});
    highlight$$ = this.input_highlight$$;

    // Vuurt zodra de onderliggende <img> klaar is met laden (ook na een ?v=-versiewissel). Wordt
    // door alle wrapper-varianten doorgegeven, zodat een parent kan wachten tot de (nieuwe) foto
    // echt zichtbaar is i.p.v. te gokken op een timer.
    loaded = output<void>({alias: 'loaded'});

    emit_onLoaded(): void {
        this.loaded.emit();
    }

    private readonly __imageVersionService = inject(ImageVersionService);

    protected _loading$$ = computed(() => this._loadingPageService.isLoading$$());

    // Effectieve cache-bust-versie: een in-sessie bewerking (annotatie/vervang/herstel) registreert
    // per guid een nieuwe versie in de ImageVersionService; die heeft voorrang op de (mogelijk oudere)
    // versie uit de DTO. Zo verversen álle boxen die dezelfde guid tonen — lijst-thumbnail, hoofdfoto
    // én detail — automatisch na een bewerking, zonder dat elke parent de versie hoeft door te geven.
    protected _effectiveVersion$$ = computed(() => {
        const __guid = this.input_guid$$();
        const __serviceVersion = __guid ? this.__imageVersionService.versions$$()[__guid] : null;
        return __serviceVersion ?? this.input_version$$() ?? null;
    });

    // Guid path to FileVariant
    protected _guidPath$$ = computed(() => {
        const __guid = this.input_guid$$();
        if (!__guid) return '';
        const __v = this._effectiveVersion$$();
        return `/api/Image/${__guid}${__v ? '?v=' + __v : ''}`;
    });
}
